import { useStyletron } from '@thanx/uikit/theme'
import type { MenuErrorSummary, PosProvider } from 'api/menuErrors'
import TabItem from 'components/Tabs/TabItem'
import TabNav from 'components/Tabs/TabNav'
import TabPane from 'components/Tabs/TabPane'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useMemo, useState } from 'react'
import Tab from 'react-bootstrap-five/Tab'
import MenuCard from './MenuCard'
import OloTabContent from './OloTabContent'

const t = buildTranslate('pos_configurations')

// Olo tab is hidden until a follow-up ticket wires the Olo branch
// content + adds telemetry. The multi-provider branch below stays
// behind this flag so re-enabling is a one-line flip; flipping it
// to true reveals the TOAST / OLO tabs from Figma 432:2630 without
// any other code change. Leaving the flag as a local constant
// (rather than an LD flag) keeps the deferred work obvious to the
// next reader and avoids the LD coordination overhead for what is
// expected to be a short window.
const SHOW_OLO_TAB = false

// Sort menus by error count desc, then notices desc, then name asc so the
// most-actionable cards land at the top of the section. Within ties the
// stable name order keeps the layout predictable across renders.
const sortByImpact = (a: MenuErrorSummary, b: MenuErrorSummary): number => {
  const errorDiff = b.configErrorsCount.error - a.configErrorsCount.error
  if (errorDiff !== 0) return errorDiff
  const noticeDiff = b.configErrorsCount.notice - a.configErrorsCount.notice
  if (noticeDiff !== 0) return noticeDiff
  return a.name.localeCompare(b.name)
}

type Props = {
  menus: MenuErrorSummary[]
  // Optional list of providers active on the merchant. When > 1 we
  // render the tab nav from Figma 432:2630; when ≤ 1 we fall through
  // to the single-provider text header from Figma 432:2419.
  providers?: PosProvider[]
  // Test seam. Defaults to the live gate (`SHOW_OLO_TAB` constant AND
  // a multi-provider response). Because `SHOW_OLO_TAB` is a compile-time
  // constant rather than an LD flag, the tab layout otherwise ships
  // dormant with no way to exercise the `true` path from a test — this
  // override lets the Toast/Olo tab nav be covered without flipping the
  // constant. When `SHOW_OLO_TAB` graduates to a real flag, drive the
  // layout off that and drop this prop.
  isMultiProvider?: boolean
}

const ToastCards = ({ menus }: { menus: MenuErrorSummary[] }) => {
  // Memoise so expanding a card (which re-renders the list) doesn't
  // re-spread + re-sort the full menu array each time.
  const sorted = useMemo(() => [...menus].sort(sortByImpact), [menus])
  return (
    <>
      {sorted.map(menu => (
        <MenuCard key={menu.name} menu={menu} />
      ))}
    </>
  )
}

const MenuErrorsList = ({
  menus,
  providers,
  isMultiProvider = SHOW_OLO_TAB && (providers?.length ?? 0) > 1,
}: Props) => {
  const [css] = useStyletron()
  const [activeTab, setActiveTab] = useState<PosProvider>('toast')

  if (!isMultiProvider) {
    // Single-provider fallback — text header above the menu cards.
    // Today this only ever fires with `toast`; if a future BE adds a
    // single-Olo non-skipped path we'll need a different empty state.
    return (
      <section
        aria-label={t('menu_errors_list.aria_label') as string}
        className="mb-l"
      >
        <h2
          className={`bold mb-m ${css({
            color: palette.grey90,
            fontSize: '20px',
            lineHeight: '28px',
          })}`}
        >
          {t('provider.toast') as string}
        </h2>
        <ToastCards menus={menus} />
      </section>
    )
  }

  return (
    <section
      aria-label={t('menu_errors_list.aria_label') as string}
      className="mb-l"
    >
      <Tab.Container
        activeKey={activeTab}
        onSelect={(key: string | null) => {
          if (key === 'toast' || key === 'olo') setActiveTab(key)
        }}
      >
        <TabNav ariaLabel={t('provider_tabs.aria_label') as string}>
          <TabItem state="toast" title={t('provider.toast') as string} />
          <TabItem state="olo" title={t('provider.olo') as string} />
        </TabNav>
        <Tab.Content className="mt-l">
          <TabPane state="toast">
            {activeTab === 'toast' && <ToastCards menus={menus} />}
          </TabPane>
          <TabPane state="olo">
            {activeTab === 'olo' && <OloTabContent />}
          </TabPane>
        </Tab.Content>
      </Tab.Container>
    </section>
  )
}

export default MenuErrorsList
